// ============================================================================
// BenefitElectionModal — records what was elected, per plan.
//
// ⛔ Does not refuse an hourly employee. It warns, and saves anyway if HR says
// so. "Hourly-paid employees aren't benefits-eligible" is today's policy and
// belongs in copy and in a view, not in a gate: the first exception to it must
// be a data-entry decision, not a code change. Same reasoning as the RPC.
// ============================================================================
import { useState, type FormEvent } from 'react';
import { X as XIcon, AlertTriangle } from 'lucide-react';
import { hrSetBenefitElection, useBenefitsPlans } from './hooks/use-hr-data';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';

const STATUSES: Array<{ value: 'pending' | 'elected' | 'waived' | 'not_eligible'; label: string }> = [
  { value: 'pending', label: 'Pending — waiting on the employee' },
  { value: 'elected', label: 'Elected — enrolled' },
  { value: 'waived', label: 'Waived — declined coverage' },
  { value: 'not_eligible', label: 'Not eligible' },
];

interface Props {
  employeeId: string;
  payType: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function BenefitElectionModal({
  employeeId,
  payType,
  onClose,
  onSaved,
}: Props) {
  const plans = useBenefitsPlans();
  const [planId, setPlanId] = useState('');
  const [status, setStatus] = useState<'pending' | 'elected' | 'waived' | 'not_eligible'>('elected');
  const [effective, setEffective] = useState(today());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hourlyWarning = payType === 'Hourly' && (status === 'elected' || status === 'pending');

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!planId) {
      setError('Pick a plan.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await hrSetBenefitElection({
        employee_id: employeeId,
        plan_id: planId,
        enrollment_status: status,
        effective_date: effective,
        notes: notes.trim() || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error('[hr] set benefit election failed', err);
      setError(errorMessage(err, 'Could not save the election.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Update benefits status"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(ev) => ev.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-tcs-text">Update benefits status</h2>
            <p className="text-xs text-tcs-text-muted">
              {employeeId} · {payType ?? 'pay type not set'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <Field label="Plan">
            <select
              value={planId}
              onChange={(ev) => setPlanId(ev.target.value)}
              className={inputCls}
              disabled={plans.loading}
            >
              <option value="">{plans.loading ? 'Loading plans…' : 'Pick a plan'}</option>
              {plans.data.map((p) => (
                <option key={p.plan_id} value={p.plan_id}>
                  {p.plan_name ?? p.plan_id}
                  {p.plan_type ? ` · ${p.plan_type}` : ''}
                </option>
              ))}
            </select>
            {!plans.loading && plans.data.length === 0 && (
              <span className="mt-1 block normal-case text-st-warn-fg-strong">
                No benefits plans are set up yet, so there is nothing to elect into.
              </span>
            )}
          </Field>

          <Field label="Status">
            <select
              value={status}
              onChange={(ev) => setStatus(ev.target.value as typeof status)}
              className={inputCls}
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Effective date">
            <input
              type="date"
              required
              value={effective}
              onChange={(ev) => setEffective(ev.target.value)}
              className={inputCls}
            />
          </Field>

          <Field label="Notes">
            <textarea
              rows={2}
              value={notes}
              onChange={(ev) => setNotes(ev.target.value)}
              className={inputCls}
            />
          </Field>
        </div>

        {hourlyWarning && (
          <p className="mt-3 flex items-start gap-2 rounded-md border border-st-warn-border bg-st-warn-bg p-2 text-xs text-st-warn-fg-strong">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Hourly-paid employees aren&rsquo;t normally benefits-eligible. This will save anyway
              &mdash; if that is deliberate, say why in the notes.
            </span>
          </p>
        )}

        {error && (
          <p className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg p-2 text-xs text-st-danger-fg-strong">
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !planId}
            className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save election'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
